import { supabase } from '@/integrations/supabase/client';

export interface SiteOffice {
  id: string;
  name: string;
  location?: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export const fetchSiteOffices = async (): Promise<SiteOffice[]> => {
  console.log('Fetching site offices...');
  
  const { data, error } = await supabase
    .from('site_offices')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching site offices:', error);
    throw error;
  }

  console.log('✅ Site offices fetched:', data?.length || 0);
  return data || [];
};

export const createCustomSiteOffice = async (name: string): Promise<SiteOffice> => {
  console.log('Creating custom site office:', name);
  
  // Validate input
  if (!name || name.trim().length < 3) {
    throw new Error('Site office name must be at least 3 characters long');
  }

  const trimmedName = name.trim();
  
  console.log('Inserting site office with data:', { name: trimmedName });

  const { data, error } = await supabase
    .from('site_offices')
    .insert([
      {
        name: trimmedName,
        description: 'Custom site office'
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating custom site office:', error);
    
    // Check if it's a duplicate name error
    if (error.code === '23505' && error.message.includes('name')) {
      throw new Error(`A site office with the name "${trimmedName}" already exists. Please choose a different name.`);
    }
    
    throw new Error(`Failed to create site office: ${error.message}`);
  }

  console.log('✅ Custom site office created successfully:', data);
  return data;
};

export const updateCustomSiteOffice = async (officeId: string, newName: string): Promise<SiteOffice> => {
  console.log('🔄 Updating custom site office:', { officeId, newName });
  
  // Validate input
  if (!newName || newName.trim().length < 3) {
    throw new Error('Site office name must be at least 3 characters long');
  }

  if (!officeId) {
    throw new Error('Office ID is required');
  }

  const trimmedName = newName.trim();
  
  // First check if the office exists
  const { data: existingOffice, error: checkError } = await supabase
    .from('site_offices')
    .select('*')
    .eq('id', officeId)
    .single();

  if (checkError) {
    console.error('❌ Error checking existing office:', checkError);
    throw new Error('Site office not found');
  }

  if (!existingOffice) {
    throw new Error('Site office not found');
  }

  console.log('📋 Existing office found:', existingOffice);
  
  const { data, error } = await supabase
    .from('site_offices')
    .update({
      name: trimmedName,
      updated_at: new Date().toISOString()
    })
    .eq('id', officeId)
    .select()
    .single();

  if (error) {
    console.error('❌ Error updating custom site office:', error);
    
    if (error.code === '23505') {
      if (error.message.includes('name')) {
        throw new Error(`A site office with the name "${trimmedName}" already exists. Please choose a different name.`);
      }
    }
    
    throw new Error(`Failed to update site office: ${error.message}`);
  }

  if (!data) {
    throw new Error('Site office not found');
  }

  console.log('✅ Custom site office updated successfully:', data);
  return data;
};

export const deleteCustomSiteOffice = async (officeId: string): Promise<boolean> => {
  console.log('🗑️ Deleting custom site office:', officeId);
  
  if (!officeId) {
    throw new Error('Office ID is required');
  }
  
  // First check if the office exists
  const { data: existingOffice, error: checkError } = await supabase
    .from('site_offices')
    .select('*')
    .eq('id', officeId)
    .single();

  if (checkError) {
    console.error('❌ Error checking existing office:', checkError);
    throw new Error('Site office not found');
  }

  if (!existingOffice) {
    throw new Error('Site office not found');
  }

  console.log('📋 Office to delete:', existingOffice);

  const { error } = await supabase
    .from('site_offices')
    .delete()
    .eq('id', officeId);

  if (error) {
    console.error('❌ Error deleting custom site office:', error);
    throw new Error(`Failed to delete site office: ${error.message}`);
  }

  console.log('✅ Custom site office deleted successfully');
  return true;
};
